import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  getCallLogs,
  getCallLogsStats,
  getCallLogMasterData,
  getCallLogById,
  patchCallLogCompletion,
} from "../api/callLogsApi";
import { getCsrfToken } from "../../../public/api/authApi";
import { getJwtRoleSlug } from "../../../public/utils/jwtRole";
import DashboardCards from "../components/staff/DashboardCards";
import OfficeCallLogFilters from "../components/staff/OfficeCallLogFilters";
import CallLogsTable from "../components/staff/CallLogsTable";
import CreateCallLogForm from "../components/staff/CreateCallLogForm";
import ViewCallLogModal from "../components/staff/ViewCallLogModal";
import ReceptionistDashboardCards from "../components/receptionist/ReceptionistDashboardCards";
import ReceptionistCallLogsTable from "../components/receptionist/ReceptionistCallLogsTable";
import ReceptionistCallLogFilters from "../components/receptionist/ReceptionistCallLogFilters";
import ReceptionistQuickCallLogForm from "../components/receptionist/ReceptionistQuickCallLogForm";
import GlobalDirectoryPaginationSection from "../../../global/directory/GlobalDirectoryPaginationSection";
import GlobalQuickJump from "../../../global/GlobalQuickJump";
import GlobalFeedback from "../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import { GlobalButton } from "../../../global/forms/GlobalFormFields";
import "../styles/callLogsDashboard.css";
import "../../user-builder/styles/userBuilder.css";
import "../../user-builder/styles/UserBuilderPage.css";
import { formatCallDateDisplay } from "../utils/callLogDates";
import { useLiveDashboardStats } from "../../../global/hooks/useLiveDashboardStats";
import { useCallLogRole } from "../utils/useCallLogRole";
import { PHONE_CALL_LOGS_SUBMODULE_SLUG } from "../utils/callLogPermissionSlugs";

const CallLogsPage = () => {
  const perms = useCallLogRole(PHONE_CALL_LOGS_SUBMODULE_SLUG);
  const canEditActions = !perms.permissionsLoading && perms.canEdit;
  const showCreate = !perms.permissionsLoading && perms.canCreate;
  const showDirectory = perms.canView;
  const hasAnyAccess = showDirectory || showCreate;
  const roleSlug = getJwtRoleSlug(
    typeof localStorage !== "undefined" ? localStorage.getItem("jwt_token") : null,
  );
  const isReceptionist = roleSlug === "receptionist";

  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [activeQuickJump, setActiveQuickJump] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedViewId, setSelectedViewId] = useState(null);
  const [editingLog, setEditingLog] = useState(null);
  const [statsReloadToken, setStatsReloadToken] = useState(0);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [columnVisibility, setColumnVisibility] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    caller_name: "",
    phone: "",
    call_type: "",
    purpose_id: "",
    is_call_completed: "",
    start_date: "",
    end_date: "",
    call_time: "",
    exact_call_date: "",
    exact_follow_up_date: "",
    remarks_contains: "",
    action_taken_contains: "",
    call_duration: "",
    contact_person_name: "",
    page: 1,
    limit: 20,
  });
  const [paginationMeta, setPaginationMeta] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });
  const [masterData, setMasterData] = useState({ types: [], purposes: [] });
  const [filterCatalogLogs, setFilterCatalogLogs] = useState([]);

  const currentUserId = useMemo(() => {
    try {
      const token = localStorage.getItem("jwt_token");
      if (!token) return null;
      const payload = JSON.parse(atob(token.split(".")[1] || ""));
      return Number(payload?.id) || null;
    } catch {
      return null;
    }
  }, []);

  const fetchMasterData = useCallback(async () => {
    try {
      const resp = await getCallLogMasterData();
      setMasterData({
        types: resp?.types || [],
        purposes: resp?.purposes || [],
      });
    } catch (err) {
      console.error("Failed to load call log master data:", err);
    }
  }, []);

  const callLogsStatsFetcher = useCallback(
    () =>
      getCallLogsStats({
        start_date: filters.start_date,
        end_date: filters.end_date,
      }),
    [filters.start_date, filters.end_date],
  );

  const { dashboardStats, loadingStats, refreshStats } = useLiveDashboardStats({
    fetcher: callLogsStatsFetcher,
    selectData: (resp) => resp?.data || resp,
    refreshDeps: [statsReloadToken, filters.start_date, filters.end_date],
  });

  const fetchLogsList = useCallback(async () => {
    setLoadingLogs(true);
    setErrorMsg("");
    try {
      const data = await getCallLogs(filters);
      setLogs(data?.data || []);
      if (data?.pagination) setPaginationMeta(data.pagination);
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setLoadingLogs(false);
    }
  }, [filters]);

  useEffect(() => {
    if (perms.permissionsLoading || !showDirectory) return;
    const t = setTimeout(() => fetchLogsList(), 400);
    return () => clearTimeout(t);
  }, [filters, fetchLogsList, perms.permissionsLoading, showDirectory]);

  useEffect(() => {
    if (perms.permissionsLoading || !showDirectory) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const data = await getCallLogs({
          page: 1,
          limit: 500,
          start_date: filters.start_date,
          end_date: filters.end_date,
        });
        if (!cancelled) setFilterCatalogLogs(data?.data || []);
      } catch {
        if (!cancelled) setFilterCatalogLogs([]);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [filters.start_date, filters.end_date, perms.permissionsLoading, showDirectory]);

  useEffect(() => {
    if (perms.permissionsLoading || !hasAnyAccess) return;
    fetchMasterData();
  }, [fetchMasterData, perms.permissionsLoading, hasAnyAccess]);

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleAddSuccess = (msg) => {
    setSuccessMsg(msg || "Call log recorded successfully");
    fetchLogsList();
    setStatsReloadToken((t) => t + 1);
    refreshStats();
  };

  const handleEditSuccess = (msg) => {
    setSuccessMsg(msg || "Call log updated successfully");
    setEditingLog(null);
    fetchLogsList();
    setStatsReloadToken((t) => t + 1);
    refreshStats();
  };

  const handleRequestEdit = async (id) => {
    try {
      setErrorMsg("");
      const resp = await getCallLogById(id);
      const log = resp?.data || resp;
      if (!log?.id) throw new Error("Unable to load call log for editing");
      setEditingLog(log);
      requestAnimationFrame(() => {
        document.getElementById("occl-create")?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (err) {
      setErrorMsg(err.message || "Failed to open call log for editing");
    }
  };

  const handleMarkFollowUpCompleted = async (callLogId) => {
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      await patchCallLogCompletion(callLogId, true, csrfToken);
      setSuccessMsg("Follow-up marked as completed");
      fetchLogsList();
      setStatsReloadToken((t) => t + 1);
      refreshStats();
    } catch (err) {
      setErrorMsg(err.message || "Failed to mark follow-up completed");
    }
  };

  const toExportRows = (items) =>
    items.map((row, idx) => ({
      "S.No": idx + 1,
      Date: row.call_date ? formatCallDateDisplay(row.call_date) : "",
      Time: row.call_time || "",
      "Caller Name": row.caller_name || "",
      "Phone Number": row.contact_number || "",
      "Call Type":
        row.call_type === true || row.call_type === 1 || String(row.call_type) === "1"
          ? "Incoming"
          : "Outgoing",
      Purpose: row.purpose_name || "",
      Action: row.action_taken || "",
      Status: row.status_name || "",
      "Handled By": row.handled_by_name || row.handled_by || "",
      "Follow-up Date": row.follow_up_date ? formatCallDateDisplay(row.follow_up_date) : "",
      Duration: row.call_duration || "",
      Remarks: row.remarks || "",
    }));

  const fetchExportRows = useCallback(async () => {
    const data = await getCallLogs({ ...filters, page: 1, limit: 500 });
    return data?.data || [];
  }, [filters]);

  const handleExportCsv = async () => {
    try {
      setErrorMsg("");
      setIsExporting(true);
      const rows = await fetchExportRows();
      if (!rows.length) {
        setErrorMsg("No call logs found for export");
        return;
      }
      const exportRows = toExportRows(rows);
      const headers = Object.keys(exportRows[0]);
      const csv = [
        headers.join(","),
        ...exportRows.map((record) =>
          headers.map((key) => `"${String(record[key] ?? "").replace(/"/g, '""')}"`).join(","),
        ),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const today = new Date();
      const y = today.getFullYear();
      const m = String(today.getMonth() + 1).padStart(2, "0");
      const d = String(today.getDate()).padStart(2, "0");
      link.download = `call-logs-${y}-${m}-${d}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      setSuccessMsg("Call logs exported to CSV successfully");
    } catch (err) {
      setErrorMsg(err.message || "Failed to export CSV");
    } finally {
      setIsExporting(false);
    }
  };

  const QUICK_JUMP_ITEMS = [
    ...(showDirectory ? [{ key: "stats", label: "Performance overview", targetId: "occl-stats" }] : []),
    ...(showCreate || (editingLog && canEditActions)
      ? [
          {
            key: "create",
            label: isReceptionist ? "Quick add" : "Capture call log",
            targetId: "occl-create",
          },
        ]
      : []),
    ...(showDirectory ? [{ key: "list", label: "Directory", targetId: "occl-list" }] : []),
  ];

  const directoryPageSize = paginationMeta.limit || 20;
  const directoryTotalPages = Math.max(1, Math.ceil((paginationMeta.total || 0) / directoryPageSize));

  const StatsCards = isReceptionist ? ReceptionistDashboardCards : DashboardCards;
  const FiltersBar = isReceptionist ? ReceptionistCallLogFilters : OfficeCallLogFilters;
  const DirectoryTable = isReceptionist ? ReceptionistCallLogsTable : CallLogsTable;

  if (perms.permissionsLoading) {
    return (
      <div className="app-screen-guard app-screen-guard--loading">
        <p>Loading access…</p>
      </div>
    );
  }

  if (!hasAnyAccess) {
    return <Navigate to="/user-login" replace />;
  }

  return (
    <div className="user-builder-shell">
      <header className="user-builder-hero">
        <div className="user-builder-hero__text">
          <p className="user-builder-hero__eyebrow">
            {isReceptionist ? "Front desk" : "Office administration"}
          </p>
          <h2 className="user-builder-hero__title">Phone Call Logs</h2>
          <p className="user-builder-hero__sub">
            Track, manage, and review phone call records with filters, directory, and exports.
          </p>
        </div>
      </header>

      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <GlobalFeedback type="error" text={errorMsg} />

      <div className="user-builder-layout">
        <div className="user-builder-layout__main">
          {showDirectory ? (
          <div id="occl-stats" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
            <StatsCards loading={loadingStats} dashboardStats={dashboardStats} />
          </div>
          ) : null}

          {(showCreate || (editingLog && canEditActions)) ? (
          <div id="occl-create" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
            {isReceptionist ? (
              <ReceptionistQuickCallLogForm masterData={masterData} onSuccess={handleAddSuccess} />
            ) : (
              <CreateCallLogForm
                masterData={masterData}
                mode={editingLog ? "edit" : "create"}
                initialValues={editingLog}
                onSuccess={editingLog ? handleEditSuccess : handleAddSuccess}
                onCancel={editingLog ? () => setEditingLog(null) : undefined}
              />
            )}
          </div>
          ) : null}

          {showDirectory ? (
          <div id="occl-list" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
            <FiltersBar
              filters={filters}
              onFilterChange={setFilters}
              masterData={masterData}
              logs={logs}
              catalogLogs={filterCatalogLogs}
              onColumnVisibilityChange={setColumnVisibility}
              onExportCsv={perms.canExport ? handleExportCsv : undefined}
            />
            <DirectoryTable
              logs={logs}
              loading={loadingLogs}
              currentUserId={isReceptionist ? undefined : currentUserId}
              onMarkFollowUpCompleted={canEditActions ? handleMarkFollowUpCompleted : undefined}
              onView={setSelectedViewId}
              onEdit={
                canEditActions && !isReceptionist ? handleRequestEdit : undefined
              }
              paginationMeta={paginationMeta}
              onPageChange={handlePageChange}
              showToolbar={false}
              columnVisibilityOverride={columnVisibility}
              showPaginationFooter={false}
            />
            <GlobalDirectoryPaginationSection
              page={paginationMeta.page}
              totalPages={directoryTotalPages}
              total={paginationMeta.total}
              pageSize={directoryPageSize}
              onPageChange={handlePageChange}
              label="Call logs directory pagination"
            />
          </div>
          ) : null}
        </div>

        <GlobalQuickJump
          asideAriaLabel="Call log sections"
          items={QUICK_JUMP_ITEMS}
          activeKey={activeQuickJump}
          onActiveChange={setActiveQuickJump}
        />
      </div>

      {selectedViewId ? (
        <ViewCallLogModal callLogId={selectedViewId} onClose={() => setSelectedViewId(null)} />
      ) : null}
    </div>
  );
};

export default CallLogsPage;
